import { BaseService, ServiceLazyLoader, ViewsManager } from '@estee/elc-service';
import { emit, events, ServiceBusLogHook } from '@estee/elc-service-bus';
import { AllUpdatedServicesTypes, root, ServiceInitializer } from '@estee/elc-universal-utils';
import { ELCLogger, TriggerType } from '@estee/elc-logging';
import { PerformanceMarker, mPulse } from '@estee/elc-telemetry';
import { onScriptError } from '../utils/onScriptError';
import { CastedDrupalProps, DrupalProps, mapConfigToProps } from './DrupalConfig';
import { getServiceBusController } from './diContainer';

const { name, version } = __serviceInfo__;

declare type IObjectLike = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
} | null;
export interface IJsAssets { jsRepoAssets: { maxRetries: number } & IObjectLike }

export class EcommApp extends BaseService {
    private performanceMarker: PerformanceMarker;
    // eslint-disable-next-line @typescript-eslint/no-parameter-properties
    constructor(private onStartService: ServiceInitializer = getServiceBusController) {
        super(name, version);
        this.initializeServiceVersions();
    }

    // @TODO: SDFEFND-4051 Removing this method will require a refactor on Drupal side
    public register = () => {
        console.warn('This method has been deprecated.');
    };

    private startWrapper = async (appConfig: DrupalProps) => {
        const ecommServiceMark = 'ecomm_service_start';

        this.performanceMarker.startMark(ecommServiceMark);

        root.env = root.env || appConfig.env || '';
        root.buid = root.buid || appConfig.businessUnitId || '';

        const props = await this.initialize(appConfig);

        await this.onStartService(appConfig);

        this.setupHttpTransport(props.defaultDomain, props.clientId);

        await this.bootstrap(props);
        this.performanceMarker.endMark(ecommServiceMark);

        await this.initializeComponentRenderer();
    };

    public async start(appConfig: DrupalProps) {
        this.logger = new ELCLogger({
            serviceName: name,
            serviceVersion: version,
            environment: root.env || appConfig.env,
            buid: root.buid || appConfig.businessUnitId
        });
        this.performanceMarker = new PerformanceMarker(this.logger, name);

        (root as Window).mPulse = new mPulse(this.logger);

        const sbLoaded = await this.isServiceBusLoaded();
        if (sbLoaded) {
            this.serviceInitializer = this.serviceInitializer || this.startWrapper;

            return super.start(appConfig);
        } else {
            this.logger.error({
                triggerType: TriggerType.runtimeError,
                message: 'Error loading ServiceBus chunks, can\'t start the rest of the services'
            });
        }
    }

    private initialize = async (appConfig: DrupalProps) => {
        const ecommInitializeMark = 'ecomm_initialize';
        this.performanceMarker.startMark(ecommInitializeMark);

        // @ts-ignore
        root.GlobalServiceBus.applyHook(ServiceBusLogHook);

        const props: CastedDrupalProps = mapConfigToProps(appConfig);
        new ServiceLazyLoader(this.logger, props).load();
        // @ts-ignore
        root.ViewsManager = new ViewsManager(props, name, version);

        // Use @ as a service and version separator if there is at least one script
        // in page that uses that convention
        const useAtSignSeparator = Array.from(document.querySelectorAll('script'))
            .map((s) => s.src)
            .filter((src) => src.indexOf(props.cdnPath) !== -1)
            .some((src) => src.split('@').length > 1);
        const versionSeparator = useAtSignSeparator ? '@' : '/';

        const replaceFunction = (originalSrc: string) => {
            const elcSrc = originalSrc.indexOf('elc-');
            const pgSrc = originalSrc.indexOf('pg-');
            const realSrc = elcSrc > -1 ? elcSrc : pgSrc;
            const sourceInfo = originalSrc.slice(realSrc);
            const [serviceName, chunkName] = sourceInfo.split('/');
            const serviceVersion = props.services[serviceName];

            return `${props.cdnPath}/${serviceName}${versionSeparator}${serviceVersion}/${chunkName}`;
        };
        root.replaceSrc = root.replaceSrc || replaceFunction;
        // @ts-ignore
        root.onerror = onScriptError(appConfig.siteUrl, this.logger);

        this.performanceMarker.endMark(ecommInitializeMark);

        return props;
    };

    private setupHttpTransport = (defaultDomain: string, clientId: string) => {
        root.HttpTransport.setBaseUrl(defaultDomain);
        root.HttpTransport.addGlobalHeaders({
            'Business-Unit': root.buid,
            ClientId: clientId || 'stardust-fe-client'
        });
    };

    private startMandatoryServices = async (props: CastedDrupalProps) => {
        try {
            const translationServiceMark = 'start_service_translations';

            this.performanceMarker.startMark(translationServiceMark);
            await this.isTranslationsLoaded();
            // @ts-ignore
            const translations = await (() => import('elc_service_translations/diContainer'))();
            await translations.serviceStarter(props);
            this.performanceMarker.endMark(translationServiceMark);

            const configServiceMark = 'start_service_config_manager';

            this.performanceMarker.startMark(configServiceMark);
            await this.isConfigManagerLoaded();
            // @ts-ignore
            const configManager = await (() => import('elc_service_config_manager/diContainer'))();
            await configManager.serviceStarter(props);
            this.performanceMarker.endMark(configServiceMark);
        } catch (error) {
            this.logger.error({
                triggerType: TriggerType.runtimeError,
                message: error.message
            });
        }
    };

    private initializeComponentRenderer = async () => {
        try {
            const crLoaded = await this.isComponentRendererLoaded();
            if (!crLoaded) {
                throw new Error('Error loading ComponentRenderer');
            }
            await root.ComponentRenderer.init();
        } catch (err) {
            this.logger.error({
                triggerType: TriggerType.render,
                message: err.message
            });
        }
    };

    private bootstrap = async (props: CastedDrupalProps) => {
        try {
            const sessionMgmtMark = 'start_session_mgmt';

            this.performanceMarker.startMark(sessionMgmtMark);
            await (this.isSessionMgmtLoaded());
            // @ts-ignore
            const mgmt = await (() => import('elc_service_session_mgmt/diContainer'))();
            await mgmt.serviceStarter(props);
            this.performanceMarker.endMark(sessionMgmtMark);

            await this.startMandatoryServices(props);
            emit(events.ECOMM_STARTED);

            const servicesMark = 'start_services';

            this.performanceMarker.startMark(servicesMark);
            await this.startServices(props);
            this.performanceMarker.endMark(servicesMark);
        } catch (error) {
            this.logger.error({
                message: error?.message,
                payload: {
                    error
                },
                triggerType: TriggerType.render
            });
        }
    };

    private startServices = async (props: CastedDrupalProps) => {
        const servicesToInitialize = Object.keys(root.ServiceRegistry).filter(
            (serviceName) =>
                serviceName !== this.name &&
                !(root.ServiceRegistry[serviceName] as AllUpdatedServicesTypes).isLazy &&
                !root.ServiceRegistry[serviceName].isStarted
        );
        for (const serviceName of servicesToInitialize) {
            const service = root.ServiceRegistry && root.ServiceRegistry[serviceName];
            if (service && !service.isStarted) {
                await service.start(props);
                emit(events.SERVICE_STARTED, { serviceName: serviceName });
            }
        }
    };

    private initializeServiceVersions() {
        // the logic related to ServiceRegistry will go away from this function once we transition all the code to root.serviceInfo
        root.getServiceVersions = () => {
            const serviceInfo = root.serviceInfo;
            const nonServiceVersions = Object.keys(serviceInfo).reduce((acc, serviceName) => {
                return { ...acc, [serviceName]: serviceInfo[serviceName].version };
            }, {});
            /* eslint-disable no-param-reassign */
            const serviceRegistryVersions = Object.keys(root.ServiceRegistry).reduce(
                (acc, serviceName) => {
                    const serviceVesion = root.ServiceRegistry[serviceName].version;
                    if (serviceVesion) {
                        return { ...acc, [serviceName]: serviceVesion };
                    }

                    return acc;
                },
                {}
            );
            /* eslint-enable no-param-reassign */
            const versions: { [key: string]: string } = {
                ...serviceRegistryVersions,
                ...nonServiceVersions
            };
            const fullInfoVersions = Object.keys(versions).reduce((acc, serviceName) => {
                const currentServiceInfo = serviceInfo[serviceName] || {};
                const branchName = currentServiceInfo.branchName || '';
                const commitHash = currentServiceInfo.commitHash || '';

                return {
                    ...acc,
                    [serviceName]: { version: versions[serviceName], branchName, commitHash }
                };
            }, {});

            console.table(fullInfoVersions);
        };
    }

    private isAssetLoaded = (asset: { succeeded: string[] }) => Boolean(asset?.succeeded?.length);

    private isServiceBusLoaded: () => Promise<boolean> = () => this.checkIfAssetHasLoaded(
        'service-bus', () => Boolean(root.GlobalServiceBus)
    );

    private isTranslationsLoaded: () => Promise<boolean> = () => this.checkIfAssetHasLoaded(
        'translations',
        // @ts-ignore
        () => true
    );

    private isConfigManagerLoaded: () => Promise<boolean> = () => this.checkIfAssetHasLoaded(
        'config-manager',
        // @ts-ignore
        () => true
    );

    private isSessionMgmtLoaded: () => Promise<boolean> = () => this.checkIfAssetHasLoaded(
        'session-mgmt',
        // @ts-ignore
        () => true
    );

    private isComponentRendererLoaded: () => Promise<boolean> = () => this.checkIfAssetHasLoaded(
        'component-renderer',
        () => Boolean(root.ComponentRenderer)
    );

    private checkIfAssetHasLoaded = (serviceAssetName: string, defaultCheck: Function) => {
        const serviceAssets = (root as Window & IJsAssets).jsRepoAssets;

        if (!serviceAssets) {
            return defaultCheck();
        }

        const asset = Object.entries(serviceAssets)
            .find(([key]) => key.includes(serviceAssetName))?.[1];

        if (!asset) {
            return false;
        }

        if (this.isAssetLoaded(asset)) {
            return true;
        }

        return () => new Promise<boolean>((resolve) => {
            const intervalId = setInterval(() => {
                if (this.isAssetLoaded(asset)) {
                    clearInterval(intervalId);

                    return resolve(true);
                }
                if ((serviceAssets.maxRetries || 3) === asset?.failed?.length) {
                    clearInterval(intervalId);

                    return resolve(false);
                }
            }, 50);
        });
    };
}